import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback
} from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';

import {
  useSoyuzUser,
  useSoyuzFlag,
  clientErrorTracker,
  useSoyuzData
} from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { useCookie } from '../../..';
import getGqlClient from '../clients/graphql/marketplace';

import {
  handleSuccessToast,
  handleErrorToast
} from '../components/utils/loginToast';
import { pushToDataLayer } from '../../utils/datalayer';

import RecaptchaProvider from './RecaptchaContext';

const UserInfoContext = createContext({
  isUserLogged: false,
  userInfo: {},
  isLoading: false,
  userId: null
});

const QUERY_USER_INFO = gql`
  query UserInfo($withChat: Boolean = false) {
    userInfo {
      id
      email
      firstLogin
      fullname
      isActivatedByEmail
      createdAt
      validationActivationSuccess
      chat @include(if: $withChat) {
        openedChannels
        unreadChannels
      }
    }
  }
`;

const getUserInfo =
  client =>
  (withChat = false) =>
    client.query({
      query: QUERY_USER_INFO,
      variables: { withChat },
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    });

const COOKIE_NEW_USER = 'sah-ui-sign-up';

export const UserInfoProvider = ({ children }) => {
  const { id: userId } = useSoyuzUser();
  const soyuzData = useSoyuzData();
  const [userInfo, setUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { getCookie, removeCookie } = useCookie();

  const canShowChat = useSoyuzFlag('ShowChatInTenantWebsite').isOn();

  const createErrorSource = useCallback(
    (source = '') => {
      const sourceBase = Object.keys(soyuzData)[0] ?? 'UserInfoProvider';

      if (source) {
        return `${sourceBase}-${source}`;
      }

      return sourceBase;
    },
    [soyuzData]
  );

  const handleUserLoginEvent = () => {
    const userLoginMethod = getCookie('sah-ui-login');

    if (userLoginMethod) {
      SoyuzAnalytics.sendGA4Event('login', {
        method: userLoginMethod,
        userId
      });
      removeCookie('sah-ui-login');
    }
  };

  const handleUserSignupEvent = () => {
    const userSignUpMethod = getCookie(COOKIE_NEW_USER);

    if (userInfo && userSignUpMethod) {
      pushToDataLayer({ tenantEmail: userInfo.email });
      SoyuzAnalytics.sendGA4Event('sign_up', { method: userSignUpMethod });
      removeCookie(COOKIE_NEW_USER);
    }
  };

  useEffect(() => {
    handleUserLoginEvent();
    handleUserSignupEvent();
  }, [userInfo]);

  useEffect(() => {
    const requestUserInfo = async () => {
      try {
        setIsLoading(true);
        const response = await getUserInfo(getGqlClient())(canShowChat);

        if (response.data) {
          setUserInfo(response.data.userInfo);
        }

        if (response.errors?.length > 0) {
          response.errors.forEach(error => {
            clientErrorTracker.error({
              error,
              source: createErrorSource('fetchUserInfo')
            });
          });
          handleErrorToast(response.errors[0]);
        }
      } catch (error) {
        clientErrorTracker.error({
          error,
          source: createErrorSource('fetchUserInfo')
        });
        handleErrorToast(error);
      }
      setIsLoading(false);
    };

    requestUserInfo();
  }, []);

  useEffect(() => {
    if (userInfo?.id) {
      handleSuccessToast(
        userInfo.firstLogin,
        userInfo.validationActivationSuccess
      );
    }
  }, [userInfo]);

  return (
    <UserInfoContext.Provider
      value={{
        isUserLogged: !!userId,
        userInfo: userInfo || {},
        isLoading,
        userId
      }}
    >
      <RecaptchaProvider>{children}</RecaptchaProvider>
    </UserInfoContext.Provider>
  );
};

UserInfoProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

export const UserInfoConsumer = UserInfoContext.Consumer;

export const useUserInfoContext = () => useContext(UserInfoContext) || {};

export const useIsUserLogged = () => {
  const userInfoContext = useUserInfoContext();

  return userInfoContext.isUserLogged;
};

export default {
  UserInfoProvider,
  UserInfoConsumer,
  useUserInfoContext,
  useIsUserLogged
};
